import React, { JSX, Suspense, useCallback, useContext } from "react";
import { Navigate, Outlet } from "react-router";
import { AxiosError } from "axios";
import { isAuthError, NoTokenError, useToken } from "@rentablo/api";
import { useMandatorContext } from "@rentablo/theming";
import { FeatureFlag } from "@rentablo/theming";
import { useFeatureFlag } from "@hooks";
import { useMyself } from "@api";
import { AuthContext } from "@providers/AuthContext";
import { AppLayoutProvider } from "@providers";
import { AppLayout } from "@components";
import { LoadingProgressBar } from "@rentablo/components";

export const ProtectedRoute = ({
  children,
  roles,
}: {
  children?: React.ReactNode;
  roles: string[];
}): React.ReactNode => {
  const { data: myself, isLoading } = useMyself();

  if (isLoading) {
    return null;
  }

  if (myself && !myself.roles?.some((role) => roles.includes(role))) {
    return <Navigate to="/" replace />;
  }

  return children ? children : <Outlet />;
};

export const OnlyAuthenticatedRoute = ({
  children,
}: {
  children?: React.ReactNode;
}): React.ReactNode => {
  const { setLoginRequired } = useContext(AuthContext);

  const handleTokenError = useCallback(
    (e: unknown) => {
      // eslint-disable-next-line no-console
      console.log(
        "only authenticated route error",
        e,
        isAuthError(e as NoTokenError | AxiosError<ApiException>)
      );
      if (!isAuthError(e as NoTokenError | AxiosError<ApiException>)) {
        return;
      }

      setLoginRequired(e as NoTokenError | AxiosError<ApiException>);
    },
    [setLoginRequired]
  );

  const { isFetching, isError } = useToken({
    onError: handleTokenError,
    key: "onlyAuthenticatedRoute",
  });

  if (isFetching || isError) {
    return null;
  }

  return children ? children : <Outlet />;
};

export const RouteWithRedirect = ({
  children,
  type,
  onlyOnProduction,
}: {
  children?: React.ReactNode;
  type: string;
  onlyOnProduction?: boolean;
}): React.ReactNode => {
  const { config } = useMandatorContext();

  if (
    onlyOnProduction &&
    import.meta.env.VITE_APP_ENVIRONMENT !== "production"
  ) {
    return children ? children : <Outlet />;
  }

  if (!config) {
    return null;
  }

  if (config.links?.[type as keyof typeof config.links]) {
    window.location.replace(config.links[type as keyof typeof config.links]!);
    return null;
  }

  return children ? children : <Outlet />;
};

export const WithFeatureFlagRoute = ({
  children,
  featureFlag,
}: {
  children?: React.ReactNode;
  featureFlag: FeatureFlag;
}): React.ReactNode => {
  const isFlagEnabled = useFeatureFlag(featureFlag);

  if (!isFlagEnabled) return <Navigate to="/" replace />;

  return children ? children : <Outlet />;
};

interface WithComponentByMandatorProps {
  default: JSX.Element;
  [key: string]: JSX.Element;
}

export const WithComponentByMandator = (
  props: WithComponentByMandatorProps
) => {
  const { mandatorSlug } = useMandatorContext();

  if (!mandatorSlug) {
    return null;
  }

  return props[mandatorSlug]
    ? (props[mandatorSlug] as JSX.Element)
    : props["default"];
};

export const WithAppLayout = ({
  children,
}: {
  children?: React.ReactNode;
}): React.ReactNode => {
  return (
    <OnlyAuthenticatedRoute>
      <AppLayoutProvider>
        <AppLayout>{children}</AppLayout>
      </AppLayoutProvider>
    </OnlyAuthenticatedRoute>
  );
};

export const withSuspense = (
  Component: React.LazyExoticComponent<() => JSX.Element>
) => (
  <Suspense fallback={<LoadingProgressBar />}>
    <Component />
  </Suspense>
);
